/*
 * ERC instant checklist items: the pop-up config, claim wiring and list
 * ordering. Unrelated to the "Instant Snapshot" checklist (ChecklistTasks.jsx).
 * Timer/expiry helpers live in util_components/instant_alert/instant_timer.
 */

import type { InstantAlertConfig } from '../../util_components/instant_alert/types';
import type { InstantEntry } from '../../util_components/instant_alert/instant_alert_entries';
import { is_terminal as isTerminal } from '../../util_components/instant_alert/task_status';

export const ERC_LIST_PATH = '/early-retention-checklist';
export const ERC_CLAIM_PATH = '/v2/early_retention_checklist/claim';
export const ERC_INSTANT_RELAY = 'erc_instant_item';
export const ERC_INSTANT_CLEARED_RELAY = 'erc_instant_item_cleared';
export const ERC_INSTANT_GONE_EVENT = 'erc-checklist:instant-gone';
export const ERC_POPUP_STORAGE_PREFIX = 'erc_instant_popup_v1';
// Mirrors goserver's claim gate: SUPER_USER without RETENTION views unclaimed.
export const ERC_CLAIM_ROLES = ['RETENTION'];

export const erc_form_href = (uuid: string): string =>
  `/early-retention-checklist-form?uuid=${encodeURIComponent(uuid)}&from=list`;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

// ERC items key on `uuid`; the pop-up holds the sales entry shape.
export const normalize_erc_item = (raw: Record<string, unknown>): InstantEntry => ({
  type: str(raw.type),
  task_uuid: str(raw.uuid) || '',
  task_type: str(raw.condition),
  task_label: str(raw.rule_label),
  student_uuid: str(raw.student_uuid),
  student_name: str(raw.student_name),
  assigned_to_ops_uuid: str(raw.assigned_to_ops_uuid),
  assigned_name: str(raw.assigned_name),
  reason: str(raw.reason),
  created_at_iso: str(raw.created_at_iso),
  instant_expires_at_iso: str(raw.instant_expires_at_iso),
  claimed_by_ops_uuid: str(raw.claimed_by_ops_uuid),
  claimed_by_name: str(raw.claimed_by_name)
});

export const ERC_INSTANT_ALERT: InstantAlertConfig = {
  pendingPath: '/v2/early_retention_checklist/instant_pending',
  alertEventPath: '/v2/early_retention_checklist/alert_event',
  alertEventBody: (uuid, eventType) => ({ uuid, event_type: eventType }),
  listPath: ERC_LIST_PATH,
  formHref: erc_form_href,
  relayItemType: ERC_INSTANT_RELAY,
  relayClearedType: ERC_INSTANT_CLEARED_RELAY,
  capabilityKey: 'is_retention',
  storagePrefix: ERC_POPUP_STORAGE_PREFIX,
  // Opposite corner to the sales pop-up so a user who gets both sees both.
  defaultCorner: 'bottom-left',
  yieldCorner: true,
  goneEvent: ERC_INSTANT_GONE_EVENT,
  normalize: normalize_erc_item,
  entryLabel: (entry) => entry.task_label || entry.task_type || 'Instant checklist',
  title: 'Instant Retention Alert',
  nounSingular: 'instant checklist',
  nounPlural: 'instant checklists',
  openLabel: 'Open checklist →',
  testIdPrefix: 'erc-instant-alert'
};

export interface ErcRow {
  uuid?: string;
  status?: string;
  is_instant?: boolean;
  created_at_iso?: string;
  claimed_by_ops_uuid?: string;
}

const is_open_instant = (row: ErcRow | null | undefined): row is ErcRow =>
  !!row && !!row.is_instant && !isTerminal(row);

// Unclaimed and open: pinned to the top with a running timer.
export const is_erc_instant_live = (row: ErcRow | null | undefined): boolean =>
  is_open_instant(row) && !row.claimed_by_ops_uuid;

export const is_erc_instant_claimed_open = (row: ErcRow | null | undefined): boolean =>
  is_open_instant(row) && !!row.claimed_by_ops_uuid;

// Live instant rows lead, oldest first; the rest keep the caller's sort.
export const sort_instant_first = <T extends ErcRow>(
  rows: T[],
  compare: (a: T, b: T) => number
): T[] =>
  rows.slice().sort((a, b) => {
    const al = is_erc_instant_live(a);
    const bl = is_erc_instant_live(b);
    if (al !== bl) return al ? -1 : 1;
    if (al) return String(a.created_at_iso || '').localeCompare(String(b.created_at_iso || ''));
    return compare(a, b);
  });
